import { useState } from 'react'
import { escapeRegExp } from 'utils'
import styled from 'styled-components'
import { Text, Button, Input, Flex, Box, QuestionHelper, FlexGap } from '@pancakeswap/uikit'
import { useTranslation } from '@pancakeswap/localization'
import { useUserSlippage } from '@pancakeswap/utils/user'
import { useUserTransactionTTL } from 'state/user/hooks'

import EBoxSm from 'components/EBoxSm'
import EBoxSmInput from 'components/EBoxSmInput'
import EButtonSm from 'components/EButtonSm'
import { variant } from 'styled-system'
import { variants } from '@pancakeswap/uikit/components/Button/types'

const StyledInput = styled(Input)`
  border: 1px solid #FFFFFF33;
  text-align: center;
  border-radius: 12px !important;
  font-size: 15px;
  height: 100%;
  width: 50px;
  padding: 0;
`

const SlippageButton = styled(Button)`
  border-radius: 8px;
  font-size: 15px;
  height: 38px;
  padding: 8px 18px;
  flex: 1;
  ${variant({
    variants: {
      [variants.PRIMARY]: {
        backgroundColor: "#49415A",
        color: "white",
      },
      [variants.TERTIARY]: {
        backgroundColor: "transparent",
        color: "textSubtle",
      },
    }
  })}
`

enum SlippageError {
  InvalidInput = 'InvalidInput',
  RiskyLow = 'RiskyLow',
  RiskyHigh = 'RiskyHigh',
}

enum DeadlineError {
  InvalidInput = 'InvalidInput',
}

const inputRegex = RegExp(`^\\d*(?:\\\\[.])?\\d*$`) // match escaped "." characters via in a non-capturing group
const THREE_DAYS_IN_SECONDS = 60 * 60 * 24 * 3

const SlippageTabs = () => {
  const [userSlippageTolerance, setUserSlippageTolerance] = useUserSlippage()
  const [ttl, setTtl] = useUserTransactionTTL()
  const [slippageInput, setSlippageInput] = useState('')
  const [deadlineInput, setDeadlineInput] = useState('')

  const { t } = useTranslation()

  const slippageInputIsValid =
    slippageInput === '' || (userSlippageTolerance / 100).toFixed(2) === Number.parseFloat(slippageInput).toFixed(2)
  const deadlineInputIsValid = deadlineInput === '' || (ttl / 60).toString() === deadlineInput

  let slippageError: SlippageError | undefined
  if (slippageInput !== '' && !slippageInputIsValid) {
    slippageError = SlippageError.InvalidInput
  } else if (slippageInputIsValid && userSlippageTolerance < 50) {
    slippageError = SlippageError.RiskyLow
  } else if (slippageInputIsValid && userSlippageTolerance > 500) {
    slippageError = SlippageError.RiskyHigh
  } else {
    slippageError = undefined
  }

  let deadlineError: DeadlineError | undefined
  if (deadlineInput !== '' && !deadlineInputIsValid) {
    deadlineError = DeadlineError.InvalidInput
  } else {
    deadlineError = undefined
  }

  const parseCustomSlippage = (value: string) => {
    if (value === '' || inputRegex.test(escapeRegExp(value))) {
      setSlippageInput(value)

      try {
        const valueAsIntFromRoundedFloat = Number.parseInt((Number.parseFloat(value) * 100).toString())
        if (!Number.isNaN(valueAsIntFromRoundedFloat) && valueAsIntFromRoundedFloat < 5000) {
          setUserSlippageTolerance(valueAsIntFromRoundedFloat)
        }
      } catch (error) {
        console.error(error)
      }
    }
  }

  const parseCustomDeadline = (value: string) => {
    setDeadlineInput(value)

    try {
      const valueAsInt: number = Number.parseInt(value) * 60
      if (!Number.isNaN(valueAsInt) && valueAsInt > 60 && valueAsInt < THREE_DAYS_IN_SECONDS) {
        setTtl(valueAsInt)
      } else {
        deadlineError = DeadlineError.InvalidInput
      }
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <Flex flexDirection="column">
      <Flex flexDirection="column" mb="24px">
        {/* <Flex mb="12px">
          <Text fontFamily="IBM Plex Sans" fontSize="18px">{t('Slippage tolerance')}</Text>
          <QuestionHelper
            text={t(
              'Setting a high slippage tolerance can help transactions succeed, but you may not get such a good price. Use with caution.',
            )}
            placement="top"
            ml="4px"
          />
        </Flex> */}
        <Flex alignItems="stretch" style={{ gap: 8 }}>
          <FlexGap gap="4px" p="4px" bg="#231F2B" borderRadius={12} flex={1}>
            <SlippageButton
              onClick={() => {
                setSlippageInput('')
                setUserSlippageTolerance(10)
              }}
              variant={userSlippageTolerance === 10 ? 'primary' : 'tertiary'}
            >
              0.1%
            </SlippageButton>
            <SlippageButton
              onClick={() => {
                setSlippageInput('')
                setUserSlippageTolerance(50)
              }}
              variant={userSlippageTolerance === 50 ? 'primary' : 'tertiary'}
            >
              0.5%
            </SlippageButton>
            <SlippageButton
              onClick={() => {
                setSlippageInput('')
                setUserSlippageTolerance(100)
              }}
              variant={userSlippageTolerance === 100 ? 'primary' : 'tertiary'}
            >
              1.0%
            </SlippageButton>
          </FlexGap>
          <FlexGap gap="4px" alignItems="center">
            <StyledInput
              scale="sm"
              inputMode="decimal"
              pattern="^[0-9]*[.,]?[0-9]{0,2}$"
              placeholder={String(userSlippageTolerance / 100)}
              value={slippageInput}
              onBlur={() => {
                parseCustomSlippage((userSlippageTolerance / 100).toFixed(2))
              }}
              onChange={(event) => {
                if (event.currentTarget.validity.valid) {
                  parseCustomSlippage(event.target.value.replace(/,/g, '.'))
                }
              }}
              isWarning={!slippageInputIsValid}
              isSuccess={![10, 50, 100].includes(userSlippageTolerance)}
            />
            %
          </FlexGap>
        </Flex>
        {!!slippageError && (
          <Text fontSize="14px" color={slippageError === SlippageError.InvalidInput ? 'red' : 'yellow'} mt="8px">
            {slippageError === SlippageError.InvalidInput
              ? t('Enter a valid slippage percentage')
              : slippageError === SlippageError.RiskyLow
                ? t('Your transaction may fail')
                : t('Your transaction may be frontrun')}
          </Text>
        )}
      </Flex>
      <Flex justifyContent="space-between" alignItems="center" mb="24px" height="46px">
        <Text fontSize="15px">{t('Tx deadlines (mins)')}</Text>
        <StyledInput
          scale="sm"
          inputMode="numeric"
          pattern="^[0-9]+$"
          isWarning={!!deadlineError}
          onBlur={() => {
            parseCustomDeadline((ttl / 60).toString())
          }}
          placeholder={(ttl / 60).toString()}
          value={deadlineInput}
          onChange={(event) => {
            if (event.currentTarget.validity.valid) {
              parseCustomDeadline(event.target.value)
            }
          }}
        />
      </Flex>
    </Flex>
  )
}

export default SlippageTabs
